import type { SortByNameOrder } from '../utils/sortByName';
import { ActivatedMobileUserMinimumData } from './mobileUser';

export interface TaskFormLockedBy {
  id: number;
  full_name: string;
  email: string;
}

export interface TaskForm {
  id: number;
  name?: string;
  code?: string;
  data?: string;
  account_id?: number;
  created_at?: Date;
  updated_at?: Date;
  index?: number;
  header?: string;
  footer?: string;
  last_info?: string;
  first_info?: string;
  template?: string;
  logo?: string;
  email_content?: string;
  hide_logo?: boolean;
  version?: string;
  hidden?: boolean | null;
  form_offset?: number;
  email_subject?: string;
  email_sender_name?: string;
  email_button_name?: string;
  email_footer?: string;
  link_token?: string;
  deleted?: boolean;
  auto_notify?: boolean;
  emails_to_notify?: string;
  collapse_subforms?: boolean;
  attach_pdf?: boolean;
  pdfgenerator_id?: number;
  pdfgenerator_active?: boolean;
  google_import?: boolean;
  share_id?: string;
  share_origin?: string;
  featured_share?: boolean;
  public_share?: boolean;
  ranking_share?: number;
  language_share?: string;
  featured_image?: string;
  description?: string;
  source?: string;
  downloads?: string;
  category?: string;
  score?: number;
  author?: string;
  translated?: boolean;
  google_edit_url?: string;
  google_published_url?: string;
  show_map?: boolean;
  form_folder_id?: number;
  skip_email?: boolean;
  signable?: boolean;
  has_pending_signatures?: boolean;
  is_scroll_active?: boolean;
  updates_locked_at: Date | null;
  updates_locked_by: TaskFormLockedBy | null;
}

export interface FormTableResponse {
  forms: TaskForm[];
  total_forms: number;
}

/** The two sortable columns of the paginated forms tables (DS-3978). */
export type FormTableSortColumn = 'name' | 'status';

/**
 * One value for both the column and its state, not a state per column: the
 * service takes a single `sort` parameter, so two columns can never claim an
 * order at once. `order: 'unsorted'` makes `column` inert.
 */
export interface FormTableSortOrder {
  column: FormTableSortColumn;
  order: SortByNameOrder;
}

export interface TemplateResponse {
  templates: TaskForm[];
  total_templates: number;
}
// Folder IDs begin with id=1, so forms without folders will be assigned id=0
export interface FormSearchResponse {
  [id: number]: {
    forms: TaskForm[];
    total_forms: number;
  };
}

export interface FormSelection extends TaskForm {
  selected: boolean;
}

export interface SignatureConfigMobileUser {
  id: number;
  email: string;
  full_name: string;
  active: boolean;
}

export interface SignatureRequester {
  id: number;
  email: string;
  name: string;
  last_name: string;
}

export enum SignatureOrderType {
  SIMULTANEOUS = 'simultaneous',
  SEQUENTIAL = 'sequential',
}

export interface SignatureOrderUser {
  id: number | string;
  is_external: boolean;
  is_location: boolean;
  question_id: number | null;
  sequence_order?: number | null;
}

export type SignerData = SignatureOrderUser & {
  name: string;
};

export interface DynamicSignatureFormQuestion {
  id: number;
  title: string;
}

export interface SignersStateGroups {
  selectedUsers: ActivatedMobileUserMinimumData[];
  externalUsersEmails: string[];
  sequentialOrder: SignatureOrderUser[][];
  simultaneousOrder: SignatureOrderUser[][];
  selectedQuestions: DynamicSignatureFormQuestion[];
}

export interface SignatureOrderConfigGroups {
  type: SignatureOrderType;
  users: SignatureOrderUser[][];
  minimum_signatures?: (number | null)[];
}

export interface RejectReason {
  id: number;
  reason: string;
}

export interface TaskFormSignatureDataGroups {
  signable: boolean;
  account_config_present: boolean;
  required_signature: boolean;
  author_must_sign: boolean;
  mobile_users: SignatureConfigMobileUser[];
  external_users: string[];
  questions: DynamicSignatureFormQuestion[];
  enabled_automated_signature: boolean;
  signature_requester?: SignatureRequester;
  order?: SignatureOrderConfigGroups;
  reject_reasons?: RejectReason[];
  reject_reasons_errors?: string[];
}

export interface TaskFormSignatureDataGroupsBody {
  signable?: boolean;
  required_signature?: boolean;
  author_must_sign?: boolean;
  mobile_user_ids?: number[];
  external_user_emails?: string[];
  signature_requester_id?: number;
  enabled_automated_signature?: boolean;
  question_ids?: number[];
  minimum_signatures?: (number | null)[];
  order?: SignatureOrderConfigGroups;
  reject_reasons?: {
    new?: string[];
    update?: { id: number; reason: string }[];
    delete?: number[];
  };
}

export interface TaskFormEmailInfo {
  email_subject: string;
  email_sender_name: string;
  email_content: string;
  email_footer: string;
  auto_notify: boolean;
  emails_to_notify: string;
  email_button_name: string;
}

export interface TemplateUseCase {
  share_id: string;
  name: string;
  featured_image: string;
  highlight?: boolean;
}

export interface RecommendedTemplate {
  name: string;
  description: string;
}

export interface ImportFormTemplateBody {
  confirmed: boolean;
  share_id: string;
  template: boolean;
  no_async?: boolean;
}

export interface ImportFormTemplateResponse {
  error?: string;
  task_form_id?: number;
  message?: string;
}

export interface DynamicSignatureFormData {
  form: {
    questions: Record<string, any>;
  };
}

export interface ValidateDynamicSignatureQuestionModificationResponse {
  is_modifying_dynamic_question: boolean;
}
